import { createContext, useContext } from 'react';
import {ILanguageContext, LanguageProviderProps} from '../types/index'

const LanguageContext = createContext<ILanguageContext | undefined>(undefined);

export const LanguageProvider = ({children, initialTranslatedText, initialLanguage}: LanguageProviderProps) => {
return (
    <LanguageContext.Provider value={{translations: initialTranslatedText, language: initialLanguage}}>
        {children}
    </LanguageContext.Provider>
)
}
// reference: https://react.i18next.com/latest/usetranslation-hook 
/**
 * Custom React hook that provides a translation function (`t`).
 *
 * This hook connects to the `LanguageContext` to access the translations
 * for the currently active language 
 * It returns a function that components can invoke to retrieve specific translated strings.
 *
 * @returns {function(key: string, fallback?: string): string} The translation function (`t`).
 * This function accepts:
 * - `key`: A string representing the ID of the translation to retrieve.
 * - `fallback` (optional): A string to return if the `key` is not found in the current translations.
 * It returns the translated string for the given `key`. If the key is not found,
 * it returns the provided `fallback` string, or the `key` itself if no fallback is specified
 * (this helps identify missing translations during development).
 *
 */
export const useTranslations = (): ((key: string, fallback?: string) => string) => {
    const context = useContext(LanguageContext);
    if (context === undefined){
        console.error('useTranslations must be used within a LanguageProvider. Ensure LanguageProvider receives initialTranslatedText and initialLanguage props. Current context:', context)
        return (key: string, fallback?: string): string => fallback || key;
    }

    const translations = context.translations;
    return (key: string, fallback?: string): string => {
        const translatedString = translations[key];
        if (translatedString == undefined) {
            return fallback || key;
        }
        return translatedString;
    };
};

/**
 * Custom React hook that retrieves the current active language of the application.
 *
 * This hook connects to the `LanguageContext` to determine the language code
 * ('EN' or 'ES') currently in use. In production builds, this will be the language
 * for which the specific static site version was generated (e.g., 'EN' for the
 * English build located at `/en/`). In development mode, this can reflect the
 * language dynamically switched via the `LanguageProvider` for testing purposes.
 *
 * @returns {'EN' | 'ES'} The current active language code ('EN' or 'ES').
 * If the hook is used outside of a `LanguageProvider` (i.e., context is undefined),
 * it logs an error and attempts to return the language defined by the
 * `VITE_BUILD_LANGUAGE` environment variable as a fallback, defaulting to 'EN'
 * if the environment variable is also not set.
 */
export const useCurrentBuildLanguage = (): 'EN' | 'ES' => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        console.error('useCurrentBuildLanguage must be used within a LanguageProvider.');
           return (import.meta.env.VITE_BUILD_LANGUAGE as 'EN' | 'ES') || 'EN';
    }
      return context.language;
}